import React, { useState, useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { Theme } from "../lib/theme";
import type { Division, ItemState } from "../types";
import type { DivTotals } from "../lib/calculations";
import { walkItems } from "../lib/calculations";
import { COL_DEFS, COL_PRESETS, DEFAULT_COLS } from "../lib/estimateDefaults";
import type { OrgCostCodes } from "../hooks/useOrgCostCodes";
import NumberInput from "../components/NumberInput";
import ResizeHandle from "../components/ResizeHandle";
import InfoTooltip from "../components/InfoTooltip";
import * as Icon from "../components/OEIcons";

interface Proposal { id: string; name: string; color: number; }

interface EstimateTabProps {
  T: Theme;
  isMobile: boolean;
  FONT: string;
  activeDivData: Division;
  /** All enabled divisions — only used to render the continuous view. */
  divisions: Division[];
  continuous: boolean;
  setContinuous: Dispatch<SetStateAction<boolean>>;
  /** Reports which division is under the sticky header so the sidebar can track it. */
  onVisibleDivChange: (divId: string|null) => void;
  confidenceRollup: { buckets: Record<string, number>; total: number; score: number };
  divTotals: DivTotals;
  proposals: Proposal[];
  getPC: (propId: string, props: Proposal[]) => { text: string; bg: string };
  globalFilter: string;
  applyDivFilter: (val: string) => void;
  showColMenu: boolean;
  setShowColMenu: Dispatch<SetStateAction<boolean>>;
  fmt: (n: number) => string;
  setActiveTab: Dispatch<SetStateAction<string>>;
  setAnalysisStatusFilter: Dispatch<SetStateAction<string>>;
  visibleCols: Record<string, boolean>;
  setVisibleCols: Dispatch<SetStateAction<Record<string, boolean>>>;
  /** Opens the Division 01 staffing plan. */
  onOpenStaffingPlan: () => void;
  /** How many staffing rows no longer match the project duration. */
  staffPlanStale: number;
  bpByDiv: Record<string, any[]>;
  bpNotedSet: Set<string>;
  setBpNotedSet: Dispatch<SetStateAction<Set<string>>>;
  bpPanelCollapsed: Record<string, boolean>;
  setBpPanelCollapsed: Dispatch<SetStateAction<Record<string, boolean>>>;
  handleAddBpToEstimate: (bp: any) => void;
  expandedSubs: Record<string, boolean>;
  setExpandedSubs: Dispatch<SetStateAction<Record<string, boolean>>>;
  /** Divisions rolled up to their total line. Continuous view only. */
  collapsedDivs: Record<string, boolean>;
  setCollapsedDivs: Dispatch<SetStateAction<Record<string, boolean>>>;
  rowsTotal: (rows: any[]) => number;
  rowsContTotal: (rows: any[]) => number;
  itemState: ItemState;
  expandedMobileRow: string | null;
  setExpandedMobileRow: Dispatch<SetStateAction<string | null>>;
  isLocked: boolean;
  upd: (id: string, field: string, value: any) => void;
  addItem: (divId: string, subId: string) => void;
  addSub: (divId: string, preset?: { name: string; costCode: string }) => void;
  removeSub: (divId: string, subId: string) => void;
  orgCostCodes: OrgCostCodes;
  addGroup: (divId: string, subId: string) => void;
  addLaborItem: (divId: string, subId: string, lr: any) => void;
  laborRates: any[];
  laborPickerSub: string | null;
  setLaborPickerSub: Dispatch<SetStateAction<string | null>>;
  editSubId: string | null;
  setEditSubId: Dispatch<SetStateAction<string | null>>;
  renameSub: (divId: string, subId: string, name: string) => void;
  descWidth: number;
  setDescWidth: Dispatch<SetStateAction<number>>;
  visColHeaders: () => [string, string][];
  renderGroupRows: (group: any, divId: string, subId: string) => React.ReactNode;
  renderItemRow: (item: any, divId: string, subId: string, inGroupId?: string | null, tradeGroupInfo?: any) => React.ReactNode;
  setImportTarget: Dispatch<SetStateAction<any>>;
  dndActive: boolean;
  dndTargetSubId: string | null;
  setDndTargetSubId: Dispatch<SetStateAction<string | null>>;
  handleSubDragOver: (e: React.DragEvent, subId: string) => void;
  handleDrop: (e: React.DragEvent) => void;
  setHoveredSubId: Dispatch<SetStateAction<string | null>>;
  snapshot: () => void;
  uid: () => string;
  setProposals: Dispatch<SetStateAction<Proposal[]>>;
  UNIT_OPTIONS: string[];
}

export default function EstimateTab({
  T, isMobile, FONT, activeDivData, divisions, continuous, setContinuous,
  onVisibleDivChange,
  confidenceRollup, divTotals, proposals, getPC,
  globalFilter, applyDivFilter, showColMenu, setShowColMenu,
  fmt, setActiveTab, setAnalysisStatusFilter,
  visibleCols, setVisibleCols, onOpenStaffingPlan, staffPlanStale,
  bpByDiv, bpNotedSet, setBpNotedSet, bpPanelCollapsed, setBpPanelCollapsed,
  handleAddBpToEstimate, expandedSubs, setExpandedSubs, collapsedDivs, setCollapsedDivs,
  rowsTotal, rowsContTotal, itemState,
  expandedMobileRow, setExpandedMobileRow, isLocked, upd,
  addItem, addSub, removeSub, orgCostCodes, addGroup, addLaborItem,
  laborRates, laborPickerSub, setLaborPickerSub,
  editSubId, setEditSubId, renameSub,
  descWidth, setDescWidth, visColHeaders,
  renderGroupRows, renderItemRow, setImportTarget,
  dndActive, dndTargetSubId, setDndTargetSubId,
  handleSubDragOver, handleDrop, setHoveredSubId,
  snapshot, uid, setProposals, UNIT_OPTIONS,
}: EstimateTabProps) {
  // Subcategory picker — predefined cost codes for the active division.
  // EstimateTab stays mounted across division switches, so this must reset
  // on division change or it stays open (and shows the wrong division's
  // catalog) when the user navigates the sidebar instead of clicking away.
  // Which division's picker is open, rather than a bare boolean — in the
  // continuous view several "+ Add Subcategory" buttons are on screen at once,
  // and a shared flag would open all of them together.
  const [subPickerDivId, setSubPickerDivId] = useState<string|null>(null);
  useEffect(()=>{setSubPickerDivId(null);},[activeDivData.id]);

  // The page header is sticky at the top of the scroll container, so the
  // division markers below it have to stick *under* it rather than at 0 —
  // otherwise they slide behind the header and vanish. Measured rather than
  // hard-coded because the header's height changes with viewport and content.
  const headerRef = useRef<HTMLDivElement|null>(null);
  const [headerH, setHeaderH] = useState(52);
  useEffect(()=>{
    const el=headerRef.current; if(!el||typeof ResizeObserver==="undefined")return;
    const sync=()=>setHeaderH(el.offsetHeight);
    sync();
    const ro=new ResizeObserver(sync);
    ro.observe(el);
    return ()=>ro.disconnect();
  },[isMobile]);

  // Scroll-spy for the continuous view: which division is currently under the
  // header. Reported up so the sidebar can highlight it — scrolling a long
  // estimate otherwise gives you no sense of where you are.
  //
  // Deliberately NOT reusing activeDiv for this. Changing activeDiv clears the
  // row/cell selection and the flagged-practices panel state (see the effects
  // keyed on it in OpenEstimate), so driving it from scroll position would wipe
  // a user's selection every time they scrolled past a division boundary.
  const [visibleDivId, setVisibleDivId] = useState<string|null>(null);
  useEffect(()=>{
    if(!continuous||isMobile){ setVisibleDivId(null); return; }
    let raf=0;
    const measure=()=>{
      raf=0;
      const hdr=headerRef.current; if(!hdr)return;
      // A stuck marker rests just under the header, so every marker at or above
      // that edge is behind us and the first one below it is still to come.
      // Measured off the header's own rect, plus a few px of slack for the
      // marker's top padding.
      const line=hdr.getBoundingClientRect().bottom+12;
      let current:string|null=null;
      for(const d of divisions){
        const el=document.getElementById(`oe-div-${d.id}`);
        if(!el)continue;
        if(el.getBoundingClientRect().top<=line) current=d.id; else break;
      }
      setVisibleDivId(current??divisions[0]?.id??null);
    };
    const onScroll=()=>{ if(!raf) raf=requestAnimationFrame(measure); };
    // Captured on the document rather than bound to the scroll container:
    // scroll events don't bubble, and the estimate has been through enough
    // layout changes that hunting for the right container by ref is fragile.
    document.addEventListener("scroll",onScroll,{capture:true,passive:true});
    measure();
    return ()=>{ document.removeEventListener("scroll",onScroll,{capture:true} as any); if(raf)cancelAnimationFrame(raf); };
  },[continuous,isMobile,divisions]);

  useEffect(()=>{ onVisibleDivChange(continuous?visibleDivId:null); },[continuous,visibleDivId,onVisibleDivChange]);

  // What the header names. In the continuous view that's whatever you have
  // scrolled to; otherwise it's the division you picked.
  const headerDiv = (continuous&&visibleDivId ? divisions.find(d=>d.id===visibleDivId) : null) || activeDivData;

  // View toggle: one division at a time, or all of them stacked. Purely
  // presentational -- same rows, same editing.
  //
  // It used to sit inside the sticky division header, after the name. In the
  // continuous view that header tracks whichever division is under the scroll
  // line, so the name kept changing width and the toggle slid left and right
  // with it -- which read, reasonably, as "this belongs to the division". It
  // controls the whole tab, so it lives on the tab-level strip above, pinned
  // to the right edge where nothing around it moves.
  const viewToggle = (
    <div style={{display:"inline-flex",alignItems:"center",gap:4,marginLeft:"auto",flexShrink:0}}>
      <span style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any}}>View</span>
      {([["single","By Division"],["all","Continuous"]] as const).map(([mode,label])=>{
        const on = (mode==="all")===continuous;
        return (
          <button key={mode} onClick={()=>setContinuous(mode==="all")}
            title={mode==="all" ? "Stack every division into one scroll" : "Show one division at a time"}
            style={{fontSize:10,padding:"4px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT,
              border:`1px solid ${on?T.borderAccent:T.borderSecondary}`,
              background:on?T.bgAccentLight:"transparent",
              color:on?T.borderAccent:T.textWeak,
              fontWeight:on?600:400}}>
            {label}
          </button>
        );
      })}
    </div>
  );

  // Guard renames that break a standard code/name pairing — mirrors the same
  // check in PropertiesTab so the rule holds wherever the user edits.
  const STD_SUBS = orgCostCodes.all;
  const nameOrigRef = useRef<{subId:string;name:string}|null>(null);
  const confirmSubNameChange = (divId:string,subId:string,costCode:string,newName:string) => {
    const orig = nameOrigRef.current;
    nameOrigRef.current = null;
    if (!orig || orig.subId !== subId || orig.name === newName) return;
    const std = STD_SUBS.find(s => s.costCode === costCode && s.name === orig.name);
    if (!std) return;
    if (!window.confirm(`"${orig.name}" is the standard name for cost code ${costCode}.\n\nRenaming it can break cost tracking across projects. Rename to "${newName}" anyway?`)) {
      renameSub(divId, subId, orig.name);
    }
  };
  return (
    <div className="fade-in">
      {/* ── Confidence rollup strip */}
      {!isMobile&&(()=>{
        const{buckets,total,score}=confidenceRollup;
        const hasConf=total>0;
        const segs=[
          {key:"Hard Bid",  color:T.positive,                          label:"Hard Bid"},
          {key:"Allowance", color:"#4a8fbf",                           label:"Allowance"},
          {key:"Other",     color:T.textWeak,                          label:"Other"},
          {key:"Needs Review",color:(T.costStatus as any)?.["Needs Review"]?.badgeText||"#bfb04a",label:"Needs Review"},
        ];
        const scoreColor=score>=75?T.positive:score>=45?"#bfb04a":T.negative;
        return(
          <div title={`Confidence score: ${score}/100 — $-weighted across cost statuses (Hard Bid = full, Allowance = ½, Other = ¼, Needs Review = 0).`}
            style={{padding:"6px 28px",background:T.bgPanel,borderBottom:`1px solid ${T.borderPrimary}`,
              display:"flex",alignItems:"center",gap:14,minHeight:36}}>
            {hasConf&&(<>
            <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,fontWeight:700,whiteSpace:"nowrap" as any}}>Confidence</div>
            <div style={{fontSize:14,fontWeight:700,color:scoreColor,fontVariantNumeric:"tabular-nums",whiteSpace:"nowrap" as any}}>{score}<span style={{fontSize:10,color:T.textMuted,fontWeight:500}}>/100</span></div>
            <div role="progressbar" aria-label="Estimate confidence" aria-valuenow={score} aria-valuemin={0} aria-valuemax={100}
              style={{flex:1,minWidth:120,height:8,background:T.bgApp,borderRadius:6,overflow:"hidden",display:"flex",border:`1px solid ${T.borderPrimary}`}}>
              {segs.map(s=>{
                const pct=total>0?(buckets[s.key]/total)*100:0;
                if(pct<=0)return null;
                return <div key={s.key} title={`${s.label}: ${pct.toFixed(1)}% (${fmt(buckets[s.key])})`} style={{width:`${pct}%`,background:s.color}}/>;
              })}
            </div>
            <div style={{display:"flex",gap:10,fontSize:10,color:T.textMuted,whiteSpace:"nowrap" as any}}>
              {segs.map(s=>{
                const pct=total>0?(buckets[s.key]/total)*100:0;
                if(pct<=0)return null;
                return(
                  <button key={s.key}
                    onClick={()=>{
                      setAnalysisStatusFilter(s.key);
                      setActiveTab("analysis");
                      const tryScroll=(delay:number,tries:number)=>{
                        setTimeout(()=>{
                          const el=document.getElementById("analysis-cost-status");
                          if(el){el.scrollIntoView({behavior:"smooth",block:"start"});}
                          else if(tries>0){tryScroll(delay*2,tries-1);}
                        },delay);
                      };
                      tryScroll(60,5);
                    }}
                    title={`Open Analysis tab and jump to ${s.label} section`}
                    style={{display:"flex",alignItems:"center",gap:4,padding:"2px 6px",margin:"-2px -2px",
                      background:"none",border:"1px solid transparent",borderRadius:3,
                      cursor:"pointer",fontFamily:FONT,fontSize:10,color:T.textMuted,whiteSpace:"nowrap" as any,
                      transition:"background .12s, border-color .12s"}}
                    onMouseEnter={e=>{(e.currentTarget as HTMLElement).style.background=`${T.borderAccent}10`;(e.currentTarget as HTMLElement).style.borderColor=`${T.borderAccent}40`;}}
                    onMouseLeave={e=>{(e.currentTarget as HTMLElement).style.background="none";(e.currentTarget as HTMLElement).style.borderColor="transparent";}}>
                    <span style={{width:7,height:7,borderRadius:"50%",background:s.color,display:"inline-block"}}/>
                    <span>{s.label} <span style={{color:T.textTertiary,fontWeight:600}}>{Math.round(pct)}%</span></span>
                  </button>
                );
              })}
            </div>
            </>)}
            {viewToggle}
          </div>
        );
      })()}
      <div ref={headerRef} style={{padding:isMobile?"8px 12px 6px":"11px 28px 9px",borderBottom:`1px solid ${T.borderPrimary}`,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:T.bgApp,zIndex:10}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div>
            <div style={{fontSize:10,color:T.textWeak,letterSpacing:".16em",textTransform:"uppercase",marginBottom:2}}>{headerDiv.code}</div>
            <div style={{fontSize:isMobile?14:17,fontWeight:600,color:T.textPrimary}}>{headerDiv.name}</div>
          </div>
          {/* Division 01 only. General conditions are staff over time, not a
              takeoff, and the duration that drives them already lives in the
              project -- this is the one place the two meet. */}
          {headerDiv.code==="Div 01"&&!isLocked&&(
            <button onClick={onOpenStaffingPlan}
              title="Price general conditions as staff over the project duration"
              style={{fontSize:10,padding:"5px 11px",borderRadius:3,cursor:"pointer",fontFamily:FONT,
                border:`1px solid ${staffPlanStale>0?T.costStatus["Needs Review"].badgeText:T.borderSecondary}`,
                background:staffPlanStale>0?`${T.costStatus["Needs Review"].badgeText}14`:"transparent",
                color:staffPlanStale>0?T.costStatus["Needs Review"].badgeText:T.textWeak,
                fontWeight:staffPlanStale>0?600:400,whiteSpace:"nowrap"}}>
              {staffPlanStale>0?"Staffing plan · schedule moved":"Staffing plan"}
            </button>
          )}
        </div>
        {isMobile?(
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:16,fontWeight:700,color:divTotals[headerDiv.id]?.total<0?T.negative:T.borderAccent}}>{fmt(divTotals[headerDiv.id]?.total)}</div>
          </div>
        ):<div style={{display:"flex",gap:14,alignItems:"center"}}>
          {proposals.slice(0,3).map(prop=>{
            const amt=divTotals[headerDiv.id]?.byProp[prop.id]||0,pc=getPC(prop.id,proposals);
            return <div key={prop.id} style={{textAlign:"right"}}>
              <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:2}}>{prop.name}</div>
              <div style={{fontSize:13,color:amt<0?T.negative:amt!==0?pc.text:T.textWeak}}>{amt!==0?fmt(amt):"--"}</div>
            </div>;
          })}
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase",marginBottom:2}}>Div Total</div>
            <div style={{fontSize:16,fontWeight:700,color:divTotals[headerDiv.id]?.total<0?T.negative:T.borderAccent}}>{fmt(divTotals[headerDiv.id]?.total)}</div>
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:3}}>
            <div style={{fontSize:10,color:globalFilter==="All"?T.textWeak:T.borderAccent,letterSpacing:".1em",textTransform:"uppercase" as const}}>
              Package Filter{globalFilter!=="All"?" · All Divisions":""}
            </div>
            <select className="filter-select" value={globalFilter} onChange={e=>applyDivFilter(e.target.value)}>
              <option value="All">All Packages</option>
              {proposals.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div style={{position:"relative"}} onClick={e=>e.stopPropagation()}>
            <button className="icon-btn" onClick={()=>setShowColMenu(v=>!v)} style={{fontSize:10,padding:"3px 9px"}}>
              Columns &#9660;
            </button>
            {showColMenu&&(
              <div className="col-menu">
                <div style={{padding:"4px 14px 6px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Presets</div>
                <div style={{display:"flex",gap:4,padding:"0 10px 8px",flexWrap:"wrap"}}>
                  {COL_PRESETS.map(pr=>{
                    const match=COL_DEFS.every(c=>!!visibleCols[c.id]===!!pr.cols[c.id]);
                    return(<button key={pr.id} onClick={()=>setVisibleCols(pr.cols)} style={{fontSize:10,padding:"3px 10px",borderRadius:3,cursor:"pointer",fontFamily:FONT,fontWeight:match?600:400,
                      border:`1px solid ${match?T.borderAccent:T.borderSecondary}`,background:match?T.bgAccentLight:"transparent",color:match?T.borderAccent:T.textWeak}}>{pr.label}</button>);
                  })}
                </div>
                <div style={{height:1,background:T.borderPrimary,margin:"0 0 4px"}}/>
                <div style={{padding:"4px 14px 6px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase"}}>Show / Hide Columns</div>
                {COL_DEFS.map(col=>(
                  <div key={col.id} className="col-menu-item" onClick={()=>setVisibleCols(p=>({...p,[col.id]:!p[col.id]}))}>
                    <div className={`col-check${visibleCols[col.id]?" on":""}`}>{visibleCols[col.id]&&<span style={{fontSize:10,color:T.textOnGold,fontWeight:700}}>✓</span>}</div>
                    <span>{col.label}</span>
                  </div>
                ))}
                <div style={{height:1,background:T.borderPrimary,margin:"4px 0"}}/>
                <div className="col-menu-item" onClick={()=>setVisibleCols(DEFAULT_COLS)} style={{fontSize:10,color:T.textWeak}}>Reset to defaults</div>
              </div>
            )}
          </div>
        </div>}
      </div>

      {/* ── Best Practices / Lessons panel */}
      {(()=>{
        const bpVisible=(bpByDiv[activeDivData.id]||[]).filter(b=>!bpNotedSet.has(b.id));
        if(!bpVisible.length)return null;
        const collapsed=bpPanelCollapsed[activeDivData.id]!==false;
        const catColors:Record<string,string>={eo:"#fc8181",scope_gap:"#f6ad55",ve:"#4fd1c5",code:"#63b3ed",owner_pref:"#b794f4"};
        const catLabels:Record<string,string>={eo:"E&O",scope_gap:"Scope Gap",ve:"VE",code:"Code Req",owner_pref:"Owner Pref"};
        return(
          <div style={{background:"#f6ad5508",borderBottom:`1px solid #f6ad5522`}}>
            <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 28px",cursor:"pointer",userSelect:"none"}}
              onClick={()=>setBpPanelCollapsed(p=>({...p,[activeDivData.id]:!collapsed}))}>
              <span style={{color:"#f6ad55",fontSize:12}}><Icon.Flag size={12}/></span>
              <span style={{fontSize:11,fontWeight:600,color:"#f6ad55"}}>
                {bpVisible.length} lesson{bpVisible.length!==1?"s":""} flagged for this division
              </span>
              <span style={{marginLeft:"auto",fontSize:10,color:T.textMuted}}>{collapsed?"▾ Show":"▴ Hide"}</span>
            </div>
            {!collapsed&&(
              <div>
                {bpVisible.map(bp=>(
                  <div key={bp.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 28px",borderTop:"1px solid #f6ad5514"}}>
                    <span style={{fontSize:10,fontWeight:700,color:catColors[bp.category]||"#888",background:(catColors[bp.category]||"#888")+"18",border:`1px solid ${(catColors[bp.category]||"#888")}44`,borderRadius:10,padding:"2px 8px",flexShrink:0,whiteSpace:"nowrap"}}>
                      {catLabels[bp.category]||bp.category}
                    </span>
                    <span style={{fontSize:12,color:T.textPrimary,flex:1,lineHeight:1.4}}>{bp.description}</span>
                    <button onClick={()=>handleAddBpToEstimate(bp)}
                      style={{flexShrink:0,background:"none",border:`1px solid ${T.borderAccent}55`,color:T.borderAccent,padding:"4px 10px",borderRadius:6,cursor:"pointer",fontFamily:FONT,fontSize:10,fontWeight:600,whiteSpace:"nowrap"}}>
                      + Add
                    </button>
                    <button onClick={()=>setBpNotedSet(prev=>new Set([...prev,bp.id]))}
                      style={{flexShrink:0,background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textMuted,padding:"4px 8px",borderRadius:6,cursor:"pointer",fontFamily:FONT,fontSize:10}}>
                      ✓ Noted
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── MOBILE ESTIMATE VIEW */}
      {isMobile&&(
        <div style={{padding:"8px 12px"}}>
          {activeDivData.subcategories.map(sub=>{
            const isOpen=expandedSubs[sub.id]!==false;
            const subTot=rowsTotal(sub.rows);
            return(
              <div key={sub.id} style={{marginBottom:12}}>
                <div onClick={()=>setExpandedSubs(p=>({...p,[sub.id]:!isOpen}))}
                  style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${T.borderSecondary}`,cursor:"pointer"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <span style={{color:T.textMuted,fontSize:10}}>{isOpen?"▾":"▸"}</span>
                    <span style={{fontSize:12,fontWeight:600,color:T.textPrimary}}>{sub.name}</span>
                  </div>
                  <span style={{fontSize:12,fontWeight:600,color:subTot>0?T.positive:subTot<0?T.negative:T.textMuted,fontVariantNumeric:"tabular-nums"}}>{fmt(subTot)}</span>
                </div>
                {isOpen&&(
                  <div style={{marginTop:4}}>
                    {[...walkItems(sub.rows)].map(item=>{
                      const li=itemState[item.id]||{};
                      const tot=(li.qty||0)*(li.unitCost||0);
                      const isExpanded=expandedMobileRow===item.id;
                      const pc=getPC(li.proposal||proposals[0]?.id||"p1",proposals);
                      return(
                        <div key={item.id} style={{borderBottom:`1px solid ${T.bgInput}`,background:isExpanded?T.bgPanel:"transparent"}}>
                          <div onClick={()=>setExpandedMobileRow(isExpanded?null:item.id)}
                            style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 8px",cursor:"pointer",minHeight:44}}>
                            <div style={{flex:1,minWidth:0,marginRight:8}}>
                              <div style={{fontSize:12,color:li.included===false?T.textMuted:T.textSecondary,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                                textDecoration:li.included===false?"line-through":"none"}}>{li.description||item.description||"New item..."}</div>
                            </div>
                            <div style={{fontSize:12,fontWeight:600,color:tot<0?T.negative:tot>0?T.positive:T.textMuted,fontVariantNumeric:"tabular-nums",flexShrink:0}}>{fmt(tot)}</div>
                          </div>
                          {isExpanded&&!isLocked&&(
                            <div style={{padding:"8px 12px 14px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Description</div>
                                <input className="input-field" value={li.description||""} onChange={e=>upd(item.id,"description",e.target.value)}
                                  style={{width:"100%",fontSize:12,padding:"8px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,fontFamily:FONT,minHeight:44}} />
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Package</div>
                                <select value={li.proposal||proposals[0]?.id||"p1"} onChange={e=>{
                                    if(e.target.value==="__new__"){snapshot();const nid=uid();setProposals(p=>[...p,{id:nid,name:`Package ${p.length+1}`,color:p.length%6}]);upd(item.id,"proposal",nid);}
                                    else upd(item.id,"proposal",e.target.value);
                                  }}
                                  style={{width:"100%",fontSize:12,padding:"8px",background:pc.bg,border:`1px solid ${pc.text}35`,color:pc.text,borderRadius:3,fontFamily:FONT,minHeight:44,cursor:"pointer"}}>
                                  {proposals.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                                  <option value="__new__">+ New Package</option>
                                </select>
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Qty</div>
                                <NumberInput value={li.qty} formula={li.qtyFormula}
                                  onChange={(v,f)=>{upd(item.id,"qty",v);upd(item.id,"qtyFormula",f||"");}}
                                  style={{width:"100%",fontSize:12,padding:"8px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,fontFamily:FONT,minHeight:44,textAlign:"right"}} />
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Unit</div>
                                <select value={li.unit||""} onChange={e=>upd(item.id,"unit",e.target.value)}
                                  style={{width:"100%",fontSize:12,padding:"8px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,fontFamily:FONT,minHeight:44}}>
                                  <option value="">—</option>
                                  {UNIT_OPTIONS.map(u=><option key={u} value={u}>{u}</option>)}
                                </select>
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Unit Cost</div>
                                <input className="input-field" inputMode="decimal" value={li.unitCost??""} onChange={e=>upd(item.id,"unitCost",parseFloat(e.target.value)||0)}
                                  style={{width:"100%",fontSize:12,padding:"8px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,fontFamily:FONT,minHeight:44,textAlign:"right"}} />
                              </div>
                              <div>
                                <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Total</div>
                                <div style={{fontSize:14,fontWeight:600,color:tot>0?T.positive:T.textMuted,padding:"8px",minHeight:44,display:"flex",alignItems:"center"}}>{fmt(tot)}</div>
                              </div>
                              <div style={{gridColumn:"1 / -1"}}>
                                <div style={{fontSize:10,color:T.textWeak,textTransform:"uppercase",letterSpacing:".1em",marginBottom:3}}>Notes</div>
                                <input className="input-field" value={li.notes||""} onChange={e=>upd(item.id,"notes",e.target.value)} placeholder="Add notes..."
                                  style={{width:"100%",fontSize:12,padding:"8px",background:T.bgInput,border:`1px solid ${T.borderSecondary}`,color:T.textSecondary,borderRadius:3,fontFamily:FONT,minHeight:44}} />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {!isLocked&&<button onClick={()=>addItem(activeDivData.id,sub.id)}
                      style={{width:"100%",padding:"10px",border:`1px dashed ${T.borderSecondary}`,borderRadius:3,background:"transparent",color:T.textWeak,fontSize:11,cursor:"pointer",fontFamily:FONT,marginTop:6}}>
                      + Add Item
                    </button>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── DESKTOP ESTIMATE VIEW */}
      {!isMobile&&<div style={{padding:"0 28px 8px"}}>
        {/* One wrapper per division so each sticky marker is scoped to its own
            section. Without one they share a containing block and every marker
            piles up on the same line instead of handing off as you scroll. */}
        {(continuous?divisions:[activeDivData]).map(divData=>{
        // Only the continuous view has a division header to collapse; in the
        // single view the division is the page, and its total is up in the
        // chrome.
        const divOpen = !continuous || collapsedDivs[divData.id]!==true;
        return (
        <div key={divData.id} style={continuous?{position:"relative",marginTop:26}:undefined}>
        {/* Division marker — only in the continuous view, where the header
            above no longer tells you which division you are scrolling through. */}
        {continuous&&(
          /* The division used to read as a peer of its own subcategories --
             14/600 against their 13/700, so the subcategory was actually the
             bolder of the two, and both carried the same 2px rule. It is the
             senior thing on the page and now looks it: twice the size, a
             brighter rule, and air above it to group what belongs to it.
             It collapses to this one line, which is what a division total
             is for. */
          <div id={`oe-div-${divData.id}`}
            onClick={()=>setCollapsedDivs(p=>({...p,[divData.id]:divOpen}))}
            title={divOpen?"Collapse this division":"Expand this division"}
            style={{display:"flex",alignItems:"baseline",gap:10,cursor:"pointer",
              padding:"10px 0 8px",borderBottom:`2px solid ${T.borderHover}`,marginBottom:2,
              position:"sticky",top:headerH,scrollMarginTop:headerH,background:T.bgApp,zIndex:3}}>
            <span style={{fontSize:13,color:T.textMuted,width:12,flexShrink:0,alignSelf:"center"}}>{divOpen?"v":">"}</span>
            <span style={{fontSize:11,color:T.textMuted,letterSpacing:".16em",textTransform:"uppercase" as any,fontWeight:700}}>{divData.code}</span>
            <span style={{fontSize:20,fontWeight:700,color:T.textPrimary,letterSpacing:"-.01em"}}>{divData.name}</span>
            <span style={{marginLeft:"auto",fontSize:16,fontWeight:700,fontVariantNumeric:"tabular-nums" as any,
              color:(divTotals[divData.id]?.total||0)<0?T.negative:T.borderAccent}}>
              {fmt(divTotals[divData.id]?.total||0)}
            </span>
          </div>
        )}
        {divOpen&&divData.subcategories.map(sub=>{
          const isOpen=expandedSubs[sub.id]!==false;
          const subTot=rowsTotal(sub.rows)+(visibleCols.contingency?rowsContTotal(sub.rows):0);
          const isRenamingSub=editSubId===sub.id;
          return (
            <div key={sub.id} style={{marginTop:16}} onMouseEnter={()=>setHoveredSubId(sub.id)} onMouseLeave={()=>setHoveredSubId(null)}>
              <div
                onDragOver={e=>handleSubDragOver(e,sub.id)}
                onDragLeave={()=>{ if(dndTargetSubId===sub.id) setDndTargetSubId(null); }}
                onDrop={handleDrop}
                style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",
                  borderBottom:dndActive&&dndTargetSubId===sub.id?`2px solid ${T.borderAccent}`:`2px solid ${T.borderSecondary}`,
                  background:dndActive&&dndTargetSubId===sub.id?T.bgAccentSubtle:undefined,
                  transition:"background .1s,border-color .1s"}}>
                <div className="sub-toggle" onClick={()=>{if(!isRenamingSub)setExpandedSubs(p=>({...p,[sub.id]:!isOpen}));}}
                  style={{display:"flex",alignItems:"center",gap:7,flex:1,minWidth:0}}>
                  <span style={{color:T.textMuted,fontSize:11,flexShrink:0}}>{isOpen?"v":">"}</span>
                  {isRenamingSub?(
                    <input autoFocus className="sub-name-input" value={sub.name}
                      onChange={e=>renameSub(divData.id,sub.id,e.target.value)}
                      onFocus={()=>{nameOrigRef.current={subId:sub.id,name:sub.name};}}
                      onBlur={e=>{confirmSubNameChange(divData.id,sub.id,sub.costCode||"",e.target.value);setEditSubId(null);}}
                      onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape")setEditSubId(null);}}
                      onClick={e=>e.stopPropagation()}/>
                  ):(
                    <span className="sub-lbl" onDoubleClick={e=>{e.stopPropagation();setEditSubId(sub.id);}}
                      title="Double-click to rename"
                      style={{fontSize:13,fontWeight:700,letterSpacing:".06em",color:T.textSubLabel,textTransform:"uppercase",cursor:"text",userSelect:"none"}}>{sub.name}</span>
                  )}
                  {sub.costCode&&<span style={{fontSize:10,color:T.textWeak,letterSpacing:".08em",marginLeft:6,fontWeight:500}}>{sub.costCode}</span>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                  <span style={{fontSize:13,color:subTot<0?T.negative:T.positive,fontWeight:600,minWidth:60,textAlign:"right"}}>{subTot!==0?fmt(subTot):"--"}</span>
                  <button onClick={e=>{e.stopPropagation();setImportTarget({divId:divData.id,divName:divData.name,subId:sub.id,subName:sub.name});}}
                    title="Import subcontractor proposal PDF"
                    style={{width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,
                      background:"none",border:`1px solid ${T.borderAccentFaint}`,color:T.textWeak,
                      borderRadius:3,cursor:"pointer",flexShrink:0,padding:0,transition:"all .15s"}}
                    onMouseEnter={e=>{e.currentTarget.style.background=T.bgAccentSubtle;e.currentTarget.style.color=T.borderAccent;e.currentTarget.style.borderColor=T.borderAccent;}}
                    onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color=`${T.borderAccent}66`;e.currentTarget.style.borderColor=T.borderAccentFaint;}}>
                    ⊕
                  </button>
                  <button className="plus-btn" onClick={e=>{e.stopPropagation();addItem(divData.id,sub.id);}}>+</button>
                  {sub.rows.length===0&&(
                    <button onClick={e=>{e.stopPropagation();removeSub(divData.id,sub.id);}}
                      title="Delete empty subcategory (Ctrl+Z to undo)"
                      style={{width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,
                        background:"none",border:`1px solid ${T.borderSecondary}`,color:T.textWeak,
                        borderRadius:3,cursor:"pointer",flexShrink:0,padding:0,transition:"all .15s"}}
                      onMouseEnter={e=>{e.currentTarget.style.background=`${T.negative}14`;e.currentTarget.style.color=T.negative;e.currentTarget.style.borderColor=`${T.negative}66`;}}
                      onMouseLeave={e=>{e.currentTarget.style.background="none";e.currentTarget.style.color=T.textWeak;e.currentTarget.style.borderColor=T.borderSecondary;}}>
                      ×
                    </button>
                  )}
                </div>
              </div>
              {isOpen&&(<>
                {/* Scrolls rather than squeezes.
                      Without a floor the columns shrink proportionally, and
                      at 1024px the unit-cost cell holds 44 pixels for a
                      number that needs 75: $18,500.00 renders as "$18,5".
                      That is not truncation, it is a different number, in
                      the one field an estimator has to defend in front of an
                      owner. The fixed columns come to about 890px and the
                      description needs room to be a description, so below
                      the floor the table scrolls sideways -- which keeps
                      every figure whole, and keeps the rows-per-screen that
                      stacking the row would have cost.

                      The overflow belongs to the pane, not to this table. A
                      scroll container per subcategory gave every section its
                      own scrollbar -- a division with six subcategories drew
                      six of them, each wedged between its last row and its
                      Add buttons -- and, worse, its own scroll position. Scroll
                      Permit Fees across to read Contingency and General
                      Conditions stayed put, so the column you were reading in
                      one band was a different column in the next. The pane
                      above already scrolls horizontally; letting it do so
                      keeps one scrollbar at the bottom of the viewport and
                      every section in the same place. */}
                {sub.rows.length>0&&(
                  <table style={{width:"100%",minWidth:1180,borderCollapse:"collapse"}}>
                    <colgroup>
                      <col style={{width:18}}/><col style={{width:26}}/><col style={{width:descWidth}}/>
                      {visibleCols.status&&<col style={{width:"12%"}}/>}
                      {visibleCols.qty&&<col style={{width:64}}/>}
                      {visibleCols.unit&&<col style={{width:62}}/>}
                      {visibleCols.unitCost&&<col style={{width:112}}/>}
                      {visibleCols.total&&<col style={{width:98}}/>}
                      {visibleCols.proposal&&<col style={{width:142}}/>}
                      {visibleCols.altType&&<col style={{width:100}}/>}
                      {visibleCols.tradeName&&<col style={{width:160}}/>}
                      {visibleCols.tradeDate&&<col style={{width:110}}/>}
                      {visibleCols.contingency&&<col style={{width:148}}/>}
                      <col style={{width:22}}/>
                    </colgroup>
                    <thead>
                      <tr style={{borderBottom:`1px solid ${T.bgHeader}`}}>
                        <th style={{width:18}}/>
                        <th style={{padding:"4px 4px"}}/>
                        <th style={{padding:"4px 6px",textAlign:"left",fontSize:10,color:T.textMuted,letterSpacing:".1em",fontWeight:500,textTransform:"uppercase",position:"relative",userSelect:"none"}}>
                          Description<ResizeHandle onResize={d=>setDescWidth(w=>Math.max(120,w+d))} color={T.resizeHandle}/>
                        </th>
                        {visColHeaders().map(([h,a])=>(
                          <th key={h} style={{padding:"4px 6px",textAlign:a as any,fontSize:10,color:T.textMuted,letterSpacing:".1em",fontWeight:500,textTransform:"uppercase"}}>
                            {h==="Status"?(
                              <span style={{display:"inline-flex",alignItems:"center",gap:3}}>
                                Status
                                <InfoTooltip T={T} FONT={FONT} text={"Hard Bid — confirmed quote\nAllowance — placeholder budget\nNeeds Review — uncertain, flag for follow-up"} size={9}/>
                              </span>
                            ):h==="Alt Type"?(
                              <span style={{display:"inline-flex",alignItems:"center",gap:3}}>
                                Alt Type
                                <InfoTooltip T={T} FONT={FONT} text={"Add Alt — optional scope addition\nDed Alt — optional scope deduction\n\nAlternates don't affect the base bid total."} size={9}/>
                              </span>
                            ):h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(()=>{
                        const _topIt=sub.rows.filter((r:any)=>r.type==="item") as any[];
                        const _tgMap=new Map<string,any>();
                        let _i=0;
                        while(_i<_topIt.length){
                          const _it=_topIt[_i];
                          const _li=itemState[_it.id]||{};
                          const _gk=(_li.tradeName||"").trim()+"|"+(_li.sourceBidId||"");
                          if(!_gk||_gk==="|"){_i++;continue;}
                          let _j=_i+1;
                          while(_j<_topIt.length){
                            const _nli=itemState[_topIt[_j].id]||{};
                            const _nk=(_nli.tradeName||"").trim()+"|"+(_nli.sourceBidId||"");
                            if(_nk!==_gk)break;
                            _j++;
                          }
                          if(_j>_i+1){
                            const _ids=_topIt.slice(_i,_j).map((r:any)=>r.id);
                            _tgMap.set(_topIt[_i].id,{isLeader:true,isMember:false,groupItemIds:_ids});
                            for(let _k=_i+1;_k<_j;_k++)_tgMap.set(_topIt[_k].id,{isLeader:false,isMember:true,groupItemIds:_ids});
                          }
                          _i=_j;
                        }
                        return sub.rows.map((row:any)=>row.type==="group"
                          ?renderGroupRows(row,divData.id,sub.id)
                          :renderItemRow(row,divData.id,sub.id,null,_tgMap.get(row.id)));
                      })()}
                    </tbody>
                  </table>
                )}
                {/* The subcategory's total sits at the END of its rows, on
                    the row that closes it out. A subtotal above the lines it
                    sums reads backwards -- the answer first, the working
                    after. The header keeps its copy for when the
                    subcategory is collapsed and there is nothing else to
                    read. */}
                <div style={{display:"flex",gap:8,padding:"6px 0 2px",alignItems:"center",flexWrap:"wrap",position:"relative"}}>
                  <button className="add-row-btn" onClick={()=>addItem(divData.id,sub.id)}>+ Add Line Item</button>
                  <button className="add-row-btn" style={{color:T.textMuted,borderColor:`${T.borderAccent}33`}} onClick={()=>addGroup(divData.id,sub.id)}>+ Add Roll-Up Group</button>
                  {laborRates.length>0&&(
                    <div style={{position:"relative"}}>
                      <button className="add-row-btn" style={{color:"#7abfaa",borderColor:"#7abfaa44"}}
                        onClick={()=>{
                          if(laborRates.length===1){addLaborItem(divData.id,sub.id,laborRates[0]);return;}
                          setLaborPickerSub(laborPickerSub===sub.id?null:sub.id);
                        }}>
                        + Add Labor
                      </button>
                      {laborPickerSub===sub.id&&(
                        <div style={{position:"absolute",top:"calc(100% + 4px)",left:0,zIndex:200,background:T.bgPanel,border:`1px solid ${T.borderHover}`,borderRadius:6,boxShadow:"0 6px 24px rgba(0,0,0,0.25)",minWidth:200,overflow:"hidden",padding:"4px 0",fontFamily:FONT}}>
                          <div style={{padding:"6px 12px 4px",fontSize:10,fontWeight:700,color:T.textWeak,letterSpacing:".08em",textTransform:"uppercase" as any,borderBottom:`1px solid ${T.borderSecondary}`}}>Select Labor Role</div>
                          {laborRates.map(lr=>(
                            <button key={lr.id}
                              onClick={()=>{addLaborItem(divData.id,sub.id,lr);setLaborPickerSub(null);}}
                              style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",background:"none",border:"none",padding:"9px 12px",cursor:"pointer",fontFamily:FONT,textAlign:"left" as any,gap:12,color:T.textSecondary,fontSize:12}}
                              onMouseEnter={e=>(e.currentTarget.style.background=T.bgHover)}
                              onMouseLeave={e=>(e.currentTarget.style.background="none")}>
                              <span>{lr.role}</span>
                              <span style={{color:T.borderAccent,fontWeight:600,fontSize:11,fontVariantNumeric:"tabular-nums"}}>${lr.rate}/hr</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {(() => {
                    const base = rowsTotal(sub.rows);
                    const cont = rowsContTotal(sub.rows);
                    if (!base && !cont) return null;
                    return (
                      <div style={{marginLeft:"auto",display:"flex",alignItems:"baseline",gap:18,paddingRight:2}}>
                        {cont > 0 && (
                          <span style={{fontSize:11,color:T.textMuted,fontVariantNumeric:"tabular-nums" as const}}
                            title="Contingency carried by the lines in this subcategory">
                            <span style={{color:T.textWeak,letterSpacing:".06em",textTransform:"uppercase" as const,fontSize:10,marginRight:6}}>Contingency</span>
                            {fmt(cont)}
                          </span>
                        )}
                        <span style={{fontSize:13,fontWeight:700,fontVariantNumeric:"tabular-nums" as const,
                          color: base < 0 ? T.negative : T.positive}}>
                          <span style={{color:T.textWeak,letterSpacing:".06em",textTransform:"uppercase" as const,fontSize:10,fontWeight:500,marginRight:6}}>Subtotal</span>
                          {fmt(base)}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              </>)}
            </div>
          );
        })}
        {divOpen&&<div style={{marginTop:14,paddingBottom:6,position:"relative"}}>
          <button className="add-sub-btn" onClick={()=>setSubPickerDivId(d=>d===divData.id?null:divData.id)}>+ Add Subcategory</button>
          {subPickerDivId===divData.id&&(()=>{
            // Standard subcategories for this division, minus ones already added
            // (matched by cost code or name so renames don't resurface duplicates)
            const catalog=orgCostCodes.byDiv[divData.id]||[];
            const existingCodes=new Set(divData.subcategories.map(s=>s.costCode));
            const existingNames=new Set(divData.subcategories.map(s=>(s.name||"").toLowerCase()));
            const available=catalog.filter(c=>!existingCodes.has(c.costCode)&&!existingNames.has(c.name.toLowerCase()));
            const pick=(preset?:{name:string;costCode:string})=>{addSub(divData.id,preset);setSubPickerDivId(null);};
            return(
              <>
                {/* click-away backdrop */}
                <div onClick={()=>setSubPickerDivId(null)} style={{position:"fixed",inset:0,zIndex:40}}/>
                <div style={{position:"absolute",left:0,bottom:"calc(100% + 6px)",zIndex:41,minWidth:300,maxWidth:380,
                  background:T.bgPanel,border:`1px solid ${T.borderSecondary}`,borderRadius:6,
                  boxShadow:`0 8px 24px ${T.bgApp}aa`,overflow:"hidden"}}>
                  <div style={{padding:"8px 14px",fontSize:10,color:T.textWeak,letterSpacing:".1em",textTransform:"uppercase" as any,fontWeight:700,borderBottom:`1px solid ${T.borderPrimary}`}}>
                    Standard — {divData.code}
                  </div>
                  <div style={{maxHeight:260,overflowY:"auto"}}>
                    {available.map(c=>(
                      <div key={c.id||`${c.divId}-${c.costCode}`} onClick={()=>pick({name:c.name,costCode:c.costCode})}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"8px 14px",cursor:"pointer",fontSize:12}}
                        onMouseEnter={e=>(e.currentTarget.style.background=T.bgHover)}
                        onMouseLeave={e=>(e.currentTarget.style.background="")}>
                        <span style={{fontFamily:"monospace",fontSize:10,color:T.textTertiary,flexShrink:0,minWidth:46}}>{c.costCode}</span>
                        <span style={{color:T.textSecondary}}>{c.name}</span>
                      </div>
                    ))}
                    {available.length===0&&(
                      <div style={{padding:"10px 14px",fontSize:11,color:T.textWeak}}>
                        {catalog.length===0?"No standard subcategories for this division.":"All standard subcategories are already in this estimate."}
                      </div>
                    )}
                  </div>
                  <div onClick={()=>pick()}
                    style={{display:"flex",alignItems:"center",gap:8,padding:"9px 14px",cursor:"pointer",fontSize:12,
                      borderTop:`1px solid ${T.borderPrimary}`,color:T.borderAccent,fontWeight:600}}
                    onMouseEnter={e=>(e.currentTarget.style.background=T.bgHover)}
                    onMouseLeave={e=>(e.currentTarget.style.background="")}>
                    + Custom subcategory…
                  </div>
                </div>
              </>
            );
          })()}
        </div>}
        </div>
        );})}
      </div>}
    </div>
  );
}
